import { z } from 'zod';
import { Cents, InvoiceDisplayStatus, InvoiceSource } from './invoicing.js';
/**
 * Chapi follows up unpaid invoices (docs/adr/0064-chapi-overdue-invoices.md, BRIEF_CHANGE_PROPOSALS 3.16). For the
 * workspace's owners and the people an owner granted "Manage invoices" only; everyone else gets 404, and the routes
 * are behind both the Invoicing and the Chapi toggles. Chapi lists the invoices past their due date (Chapsi, tracked
 * Xero and recorded ones), checks whether the work they bill is really finished and whether the client is waiting on
 * the team, says in one line what is probably holding payment up, and drafts a reminder the person reviews. It never
 * sends anything on its own.
 */
/** A tracked Xero invoice id or a Chapsi/recorded invoice id: both are uuids. */
export const InvoiceKey = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'An invoice id');
export const OverdueReasonCode = z.enum([
    /** Not assigned to a project, milestone or tasks, and no client to look at: nothing to check. */
    'not_linked',
    /** Tasks the invoice bills were reopened after it was issued. */
    'work_reopened',
    /** Tasks the invoice bills are still open. */
    'work_open',
    /** The client wrote and nobody from the team answered. */
    'client_waiting',
    /** The work is done and the client has said nothing since the invoice went out. */
    'no_reply',
    /** The work is done and the client has been in touch since; probably just slow to pay. */
    'all_done',
]);
/** What Chapi checked, as counts and dates. Never a task title, a message body or a rate. */
export const OverdueSignals = z.object({
    /** Where the work was looked for: the invoice's tasks (or its milestone's), its project, or nowhere. */
    workScope: z.enum(['tasks', 'project', 'none']),
    /** The milestone code ("M3") when the tasks are one milestone's. */
    milestoneCode: z.string().nullable(),
    /** The project key ("SIM") when the work is a project's. */
    projectKey: z.string().nullable(),
    tasks: z.number().int().min(0),
    openTasks: z.number().int().min(0),
    /** Open again after having been done, since the invoice was issued. */
    reopenedTasks: z.number().int().min(0),
    /** Client messages (in channels Chapi may read for this person) and ticket emails with no answer from the team. */
    unansweredClientMessages: z.number().int().min(0),
    /** The oldest of those: how long the client has been waiting. */
    clientWaitingSince: z.string().nullable(),
    lastClientMessageAt: z.string().nullable(),
    /** The last monthly report emailed to the client (docs/adr/0042). */
    lastClientUpdateAt: z.string().nullable(),
    /** The last reminder for this invoice sent through Chapsi. */
    lastReminderAt: z.string().nullable(),
});
export const OverdueInvoice = z.object({
    source: InvoiceSource,
    id: InvoiceKey,
    number: z.string().nullable(),
    status: InvoiceDisplayStatus,
    issueDate: z.string().nullable(),
    dueDate: z.string(),
    daysOverdue: z.number().int().min(1),
    client: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    project: z.object({ id: z.uuid(), name: z.string(), key: z.string() }).nullable(),
    milestone: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).nullable(),
    /** Only ever sent to people who manage invoices (the only people these routes answer). */
    amountCents: Cents.nullable(),
    currency: z.string().nullable(),
    signals: OverdueSignals,
    reason: z.object({ code: OverdueReasonCode, text: z.string() }),
    /** The client's billing contacts (support plan) and support contacts Chapsi can email; empty: copy it instead. */
    recipients: z.array(z.email()).max(10),
});
export const OverdueNudge = z.enum(['off', 'weekly', 'daily']);
export const OverdueInvoiceList = z.object({
    items: z.array(OverdueInvoice),
    /** More overdue invoices than the list shows (the oldest-due come first). */
    truncated: z.boolean(),
    /** Whether Chapi can draft right now (a model on this server, budget left). Checking needs no model. */
    canDraft: z.boolean(),
    /** The owner's nudge setting, and whether this person may change it (owners only). */
    nudge: OverdueNudge,
    canChangeNudge: z.boolean(),
});
export const InvoiceReminderInput = z.object({ source: InvoiceSource, invoiceId: InvoiceKey });
/** A draft for the person to edit, copy or send. Nothing was sent or saved. */
export const InvoiceReminderDraft = z.object({
    subject: z.string(),
    text: z.string(),
    recipients: z.array(z.email()).max(10),
});
/**
 * Sends the person's reviewed text to some of the invoice's recipients through the workspace's client email path.
 * `reviewed: true` is the explicit confirmation; the addresses must be ones the list offered.
 */
export const SendInvoiceReminderInput = InvoiceReminderInput.extend({
    to: z.array(z.email().max(254)).min(1).max(10),
    subject: z.string().trim().min(1).max(200),
    text: z.string().trim().min(1).max(5000),
    reviewed: z.literal(true),
});
export const SendInvoiceReminderResponse = z.object({ sent: z.number().int().min(0), sentAt: z.string() });
/** "3 invoices overdue": the notification title. A count, never an amount. */
export function overdueNudgeTitle(count) {
    return count === 1 ? '1 invoice overdue' : `${count} invoices overdue`;
}
//# sourceMappingURL=chapi-invoices.js.map